import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import styled from 'styled-components';
import { useKycDocumentCardController } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/KycDocumentCard/useKycDocumentCardController.tsx';
import { Badge, Divider, Flex, Menu, rem, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { EditKycDocumentDrawer } from './EditKycDocumentDrawer/EditKycDocumentDrawer.tsx';
import { FormTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/useKYCDocumentsController.ts';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { PreviewFormTemplateModal as PreviewFormModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/KycDocumentCard/PreviewFormTemplateModal/PreviewFormTemplateModal.tsx';
import type { KycPlacementValue } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/SignaturePlacement/kycSignaturePlacement.types.ts';
import { CustomDetailCategory } from '@dealstack/shared/lib/generated';

interface Props {
	formTemplate: FormTemplate;
	removeFormTemplate: (formTemplateIndexInFieldArray: number) => void;
	onDelete: () => void;
	onUpdateTemplate?: (updates: {
		name?: string;
		instruction?: string;
		category?: CustomDetailCategory | null;
		requiresSignature?: boolean;
		useManualSignaturePlacement?: boolean;
		pendingPlacement?: KycPlacementValue | null;
	}) => void;
	onArchiveTemplate?: (archived: boolean) => void;
	formTemplateFieldKey: string;
	showCategorySelect?: boolean;
	className?: string;
	isArchiveMode?: boolean;
}

const KycDocumentCard = ({
	formTemplate,
	onUpdateTemplate,
	onArchiveTemplate,
	formTemplateFieldKey,
	onDelete,
	showCategorySelect = false,
	className,
	isArchiveMode = false,
}: Props) => {
	const tMipCreate = useTranslationMipCreate();

	const {
		onPreviewDocument,
		showEditDrawer,
		setShowEditDrawer,
		showDocumentPreview,
		setShowDocumentPreview,
		fileBlob,
	} = useKycDocumentCardController(formTemplate);

	const handleArchiveClick = () => {
		onArchiveTemplate?.(!formTemplate.archived);
	};

	return (
		<div className={className} data-testid={`kyc-document-card-${formTemplate.id}`}>
			<Flex direction="column" justify="space-between" h="100%" gap="1.3rem" p="0.75rem">
				<Flex justify="space-between">
					<div />

					<div
						onClick={(event) => {
							event.stopPropagation();
						}}
					>
						<Menu width={160} position="left" offset={0}>
							<Menu.Target>
								<div
									data-testid={TEST_ELEMENT_IDS.MIP_CREATE_KYC_DOCUMENT_CARD_MENU}
								>
									<Icon variant="more" size="lg" />
								</div>
							</Menu.Target>

							<Menu.Dropdown
								sx={(theme) => ({
									backgroundColor: theme.colors.gray[0],
									padding: '0.5rem',
									boxShadow: '0px 4px 8px 0px #DCDFE0',
								})}
							>
								<Menu.Item p="0.25rem" onClick={onPreviewDocument}>
									<Text size="B6Medium">
										{tMipCreate('kyc.documents.card.preview_form')}
									</Text>
								</Menu.Item>
								<>
									{!isArchiveMode && (
										<Menu.Item
											p="0.25rem"
											data-testid={
												TEST_ELEMENT_IDS.MIP_CREATE_KYC_DOCUMENT_CARD_EDIT
											}
											onClick={() => {
												setShowEditDrawer(true);
											}}
										>
											<Text size="B6Medium">
												{tMipCreate('kyc.documents.card.edit_information')}
											</Text>
										</Menu.Item>
									)}

									{onArchiveTemplate && (
										<Menu.Item p="0.25rem" onClick={handleArchiveClick}>
											<Text size="B6Medium">
												{formTemplate.archived
													? tMipCreate(
															'kyc.documents.card.unarchive_document'
														)
													: tMipCreate(
															'kyc.documents.card.archive_document'
														)}
											</Text>
										</Menu.Item>
									)}

									<Divider />
									<Menu.Item p="0.25rem" onClick={onDelete}>
										<Text size="B6Medium" c="red.6">
											{tMipCreate('kyc.documents.card.delete_document')}
										</Text>
									</Menu.Item>
								</>
							</Menu.Dropdown>
						</Menu>
					</div>
				</Flex>

				<div>
					<Text size="B6Medium" style={{ userSelect: 'none' }} truncate lineClamp={3}>
						{formTemplate.name}
					</Text>
					{formTemplate.isPreFillable && (
						<Badge
							variant="filled"
							size="xs"
							color="gray.4"
							c="dark.4"
							rightSection={<Icon variant="i-circle" />}
							px="0.5rem"
							py={0}
						>
							<Text
								fz={11}
								fw={400}
								style={{ whiteSpace: 'nowrap', userSelect: 'none' }}
							>
								{tMipCreate('kyc.documents.card.smart_form')}
							</Text>
						</Badge>
					)}
				</div>
			</Flex>
			<EditKycDocumentDrawer
				opened={showEditDrawer}
				onClose={() => {
					setShowEditDrawer(false);
				}}
				formTemplate={formTemplate}
				formTemplateFieldKey={formTemplateFieldKey}
				onPreviewDocument={onPreviewDocument}
				onUpdateTemplate={onUpdateTemplate}
				showCategorySelect={showCategorySelect}
			/>
			{showDocumentPreview && (
				<PreviewFormModal
					opened={showDocumentPreview}
					onClose={() => setShowDocumentPreview(false)}
					fileBlob={fileBlob}
					formTemplate={formTemplate}
				/>
			)}
		</div>
	);
};

const Styled = styled(KycDocumentCard)`
	width: ${rem(160)};
	height: ${rem(152)};
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 0.5rem;
`;

export { Styled as KycDocumentCard };
